import React from 'react';
import { formatNameForLegend } from '../../../utils/formatUtils';

/**
 * Componente de legenda para o calendário
 * 
 * @param {Object} props - Propriedades do componente
 * @param {Array} props.resources - Lista de recursos (profissionais)
 * @returns {JSX.Element} - Elemento React
 */
const TimelineLegend = ({ resources }) => {
  /**
   * Formata o nome do profissional para exibição na legenda
   * Limita o tamanho do nome para manter a apresentação limpa
   * 
   * @param {string} name - Nome do profissional
   * @returns {string} - Nome formatado para exibição
   */
  const formatResourceName = (name) => {
    if (!name) return '';
    return name.length > 18 ? `${name.slice(0, 17)}…` : name;
  };

  return (
    <div className="timeline-legend">
      <h4 className="text-sm font-medium mb-2">Legenda:</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs mb-2">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-yellow-400 mr-1 rounded"></div>
          <span>Tempo de preparação</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-400 mr-1 rounded"></div>
          <span>Tempo de finalização</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-amber-500 mr-1 rounded"></div>
          <span>Pendente de confirmação</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-500 mr-1 rounded"></div>
          <span>Cancelado</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-gray-500 mr-1 rounded"></div>
          <span>Horário bloqueado</span>
        </div>
      </div>
      
      <h4 className="text-sm font-medium mb-2 mt-3">Origem dos Agendamentos:</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs mb-3">
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span>Agendado pelo atendente</span>
        </div>
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <span>Agendado pelo cliente (celular)</span>
        </div>
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span>Agendado pelo cliente (computador)</span>
        </div>
      </div>
      
      <h4 className="text-sm font-medium mb-2 mt-3">Status:</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs mb-3">
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>Atendimento confirmado</span>
        </div>
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          <span>Cancelado</span>
        </div>
        <div className="flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Aguardando</span>
        </div>
      </div>
      
      <h4 className="text-sm font-medium mb-2 mt-3">Profissionais:</h4>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        {resources?.map((resource, index) => (
          <div 
            key={index} 
            className="flex items-center" 
            title={resource.title}
          >
            <div 
              className="w-3 h-3 rounded-full mr-1 flex-shrink-0" 
              style={{ backgroundColor: resource.color }}
            />
            <span className="truncate max-w-full">
              {formatNameForLegend(resource.title)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TimelineLegend; 